import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import DataCollectionCube from './DataCollectionCube';
import AnalysisProcessing from './AnalysisProcessing';
import VisualizationInsights from './VisualizationInsights';


export interface AccordionItem {
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface HorizontalAccordionProps {
  items: AccordionItem[];
}

export const HorizontalAccordion: React.FC<HorizontalAccordionProps> = ({ items }) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(0);

  const handleClick = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const activeWidth = 70;
  const inactiveWidth =
    activeIndex !== null ? (100 - activeWidth) / (items.length - 1) : 100 / items.length;

  const get3DScene = (index: number) => {
    switch (index) {
      case 0:
        return (
          <Canvas orthographic
      camera={{
        zoom: 50,
        position: [10, 10, 10]
      }}>
            <ambientLight intensity={0.5} />
            <pointLight position={[10, 10, 10]} intensity={1} />
            <pointLight position={[-10, -10, -10]} intensity={0.5} />
            <DataCollectionCube />
          </Canvas>
        );
      case 1:
        return (
          <Canvas orthographic camera={{ zoom: 70, position: [10, 10, 10], near: 0.1, far: 1000 }}>
            <ambientLight intensity={0.5} />
            <pointLight position={[10, 10, 10]} intensity={1} />
            <pointLight position={[-10, -10, -10]} intensity={0.5} />
            <OrbitControls enableZoom={false} />
            <AnalysisProcessing />
          </Canvas>
        );
      case 2:
        return (
          <Canvas orthographic camera={{ zoom: 50, position: [17, 10, 10], near: 0.1, far: 1000 }}>
            <ambientLight intensity={0.5} />
            <pointLight position={[10, 10, 10]} intensity={1} />
            <pointLight position={[-10, -10, -10]} intensity={0.5} />
            <VisualizationInsights />
          </Canvas>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex w-full overflow-hidden h-[400px]">
      {items.map((item, index) => {
        const isActive = activeIndex === index;
        const width = isActive ? `${activeWidth}%` : `${inactiveWidth}%`;

        return (
          <motion.div
            key={index}
            onClick={() => handleClick(index)}
            className="relative cursor-pointer border border-[#1a1a1a] bg-[#0D0D0E] overflow-hidden h-full"
            animate={{ width }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
          >
            <motion.div
              className="absolute top-4 right-4"
              animate={{ rotate: isActive ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronRight className="w-6 h-6 text-gray-400" />
            </motion.div>

            <div className={`p-8 h-full flex flex-col justify-center ${isActive ? "pr-[33%]" : ""}`}>
              <div className="mb-4">{item.icon}</div>
              <h3 className="text-xl font-bold mb-2">{item.title}</h3>
              {isActive && (
                <motion.p
                  className="text-gray-400 mt-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  {item.description}
                </motion.p>
              )}
            </div>

            <AnimatePresence>
              {isActive && (
                <motion.div
                  className="absolute top-0 right-0 h-full w-1/3 flex items-center justify-center"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 50 }}
                  transition={{ duration: 0.5 }}
                >
                  {get3DScene(index)}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
};